// src/components/subscription/PayPalModal.js
import React from 'react';
import { XMarkIcon, CreditCardIcon } from '@heroicons/react/24/solid';
import PayPalButton from './PayPalButton';
import ActionButton from '../common/ActionButton';
import { OptimizedImage } from '../../utils/imageManager';

const PayPalModal = ({ onClose, selectedPlan, onSuccess, onError, style, onMouseDown }) => {
  return (
    <div
      className="bg-background-secondary rounded-2xl shadow-2xl w-full max-w-md border border-border border-t-4 overflow-hidden"
      style={{
        borderTopColor: 'var(--color-primary)',
        ...style,
      }}
      onMouseDown={onMouseDown}
    >
      {/* Glassmorphic header */}
      <div
        className="relative p-4 flex justify-between items-center overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, var(--color-primary)15, var(--color-secondary)25)',
        }}
      >
        {/* Glassmorphic overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-70 dark:from-white/5 dark:opacity-40" />

        {/* Content */}
        <div className="relative z-10 flex items-center">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 backdrop-blur-sm border border-white/20"
            style={{
              background: 'var(--color-primary)',
              boxShadow: '0 4px 12px var(--color-primary)30',
            }}
          >
            <CreditCardIcon className="h-5 w-5 text-white drop-shadow-sm" />
          </div>
          <h3 className="text-lg font-bold text-content-primary">Complete Subscription</h3>
        </div>

        <button onClick={onClose} className="relative z-10 p-1 rounded-full text-content-secondary hover:bg-background-primary transition-colors">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      <div className="p-6 text-center space-y-6">
        {/* Character image with glassmorphic background */}
        <div className="relative inline-block">
          <div
            className="
              w-36 h-36 rounded-full backdrop-blur-sm border border-border
              flex items-center justify-center overflow-hidden mx-auto
            "
            style={{
              background: 'var(--color-background-primary)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
            }}
          >
            {/* Subtle glassmorphic overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30 rounded-full" />
            <OptimizedImage 
              imageKey="ACTION_CORRECT" 
              className="relative z-10 w-28 h-28 object-contain" 
              fallback={<span className="text-6xl">🎉</span>}
            />
          </div>
        </div>

        {/* Plan description */}
        <div
          className="
            relative p-4 rounded-2xl backdrop-blur-sm border border-border 
            overflow-hidden
          "
          style={{
            background: 'linear-gradient(135deg, var(--color-accent)08, var(--color-accent)15)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
          }}
        >
          {/* Glassmorphic overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />

          {/* Content */}
          <div className="relative z-10">
            <p className="text-content-secondary leading-relaxed">
              You are subscribing to the{' '}
              <span
                className="px-2 py-1 rounded-lg text-sm font-bold text-white mx-1"
                style={{
                  background: 'var(--color-primary)',
                  boxShadow: '0 2px 8px var(--color-primary)40',
                }}
              >
                {selectedPlan}
              </span>{' '}
              plan. Enjoy exclusive premium features!
            </p>
          </div>
        </div>

        {/* PayPal Button */}
        <div className="pt-2">
          <PayPalButton selectedPlan={selectedPlan} onSuccess={onSuccess} onError={onError} />
        </div>
      </div>

      {/* Footer with glassmorphic button */}
      <div
        className="
          px-6 py-4 flex justify-end border-t border-border
        "
        style={{
          background: 'var(--color-background-primary)',
        }}
      >
        <ActionButton
        variant="glassy"
        type="button"
        onClick={onClose}
        color="neutral"
        className="w-full px-6 py-3 text-white"
      >
        Cancel
      </ActionButton>
      </div>
    </div>
  );
};

export default PayPalModal;
